import {
  CASE_OWNER_STATUS_ITEMS,
  CASE_OWNER_WORK_ITEMS,
  COMPANY_ADMIN_SIDEBAR_GROUPS,
  COORDINATOR_WORK_ITEMS,
  APPLICANT_ONBOARDING_GROUPS,
  ONBOARDING_SYSTEM_GROUPS,
  REQUESTOR_WORK_ITEMS
} from './config_sidebar_constants';
import {
  build_case_owner_profile,
  build_initials,
  compact_text,
  get_badge_count,
  is_applicant_role,
  is_case_owner_role,
  is_coordinator_role,
  is_present,
  is_requestor_role,
  resolve_selected_journey_id,
  resolve_selected_journey_label
} from './config_sidebar_computed_helpers';

function build_selected_journey_item(selected_journey_id, selected_journey_label) {
  if (!selected_journey_label) return null;

  const has_selected_journey = Boolean(selected_journey_id);
  const fallback_id = get_selected_journey_id(selected_journey_id, has_selected_journey);
  const meta = get_selected_journey_meta(has_selected_journey);
  const icon = get_selected_journey_icon(has_selected_journey);

  return {
    id: fallback_id,
    label: selected_journey_label,
    meta,
    icon,
    icon_tone: 'teal',
    count: '0'
  };
}

function get_selected_journey_id(selected_journey_id, has_selected_journey) {
  if (has_selected_journey) {
    return selected_journey_id;
  }

  return 'all-journeys';
}

function get_selected_journey_meta(has_selected_journey) {
  if (has_selected_journey) {
    return 'Selected journey';
  }

  return 'Manager / admin view across journeys';
}

function get_selected_journey_icon(has_selected_journey) {
  if (has_selected_journey) {
    return 'bi-person';
  }

  return 'bi-grid';
}

function build_default_journey_item() {
  const default_item = {
    id: 'all-journeys',
    label: 'All Journeys',
    meta: 'Manager / admin view across journeys',
    icon: 'bi-grid',
    icon_tone: 'teal',
    count: '0'
  };

  return default_item;
}

function item_is_not_all_journeys(item) {
  return item.id !== 'all-journeys';
}

function item_is_all_journeys(item) {
  return item.id === 'all-journeys';
}

function item_matches_journey_id(item, selected_journey_id) {
  return String(item.id) === selected_journey_id;
}

function add_badge_count(item, badges) {
  const count = get_badge_count(badges, item.id);
  return count === undefined ? item : { ...item, count };
}

function get_group_badges(group = {}, scoped_badges = {}, journey_badges = {}) {
  if (group.id === 'journeys') {
    return journey_badges;
  }

  return scoped_badges;
}

function get_onboarding_group_items(group, journey_items, gate_items) {
  if (group.id === 'journeys') {
    return journey_items.filter(item_is_not_all_journeys);
  }

  if (group.id === 'gates') {
    return gate_items;
  }

  return group.items;
}

function build_applicant_onboarding_groups(badges, gate_items) {
  const count = get_badge_count(badges, 'my-onboardings');
  const groups = APPLICANT_ONBOARDING_GROUPS.map((group) => ({
    ...group,
    items: group.items.map((item) => (count === undefined ? item : { ...item, count }))
  }));
  if (Array.isArray(gate_items) && gate_items.length) {
    groups.push({ id: 'applicant-gates', label: 'Gates', items: gate_items });
  }
  return groups;
}

function build_onboarding_group(group, journey_items, gate_items, badges) {
  const items = get_onboarding_group_items(group, journey_items, gate_items)
    .map(function map_badged_item(item) {
      return add_badge_count(item, badges);
    });

  return { ...group, items };
}

function find_route_journey(items, selected_journey_id) {
  return items.find(function match_item(item) {
    return item_matches_journey_id(item, selected_journey_id);
  });
}

function resolve_current_journey(items, selected_journey_id, selected_journey_label) {
  const route_item = find_route_journey(items, selected_journey_id);
  const query_item = build_selected_journey_item(selected_journey_id, selected_journey_label);
  const all_journeys_item = items.find(item_is_all_journeys);
  const current_journey = route_item || query_item || all_journeys_item || build_default_journey_item();

  return current_journey;
}

export const configSidebarComputed = {
  sidebar_groups() {
    return COMPANY_ADMIN_SIDEBAR_GROUPS;
  },
  current_role() {
    return this.$store.getters.currentRole || '';
  },
  current_lead_name() {
    return this.$store.getters.currentLeadName || '';
  },
  current_user_display_name() {
    return this.current_lead_name || 'User';
  },
  current_user_initials() {
    return build_initials(this.current_user_display_name, 'U');
  },
  current_user_role_label() {
    return this.current_role || 'Logged in user';
  },
  sidebar_meta_line() {
    return ['ROLE', this.current_role].map(compact_text).filter(is_present).join(' - ');
  },
  uses_case_owner_sidebar() {
    return is_case_owner_role(this.current_role);
  },
  uses_requestor_sidebar() {
    return is_requestor_role(this.current_role);
  },
  uses_coordinator_sidebar() {
    return is_coordinator_role(this.current_role);
  },
  uses_onboarding_system_sidebar() {
    return Boolean(this.$route.meta?.onboardingSystem);
  },
  show_onboarding_journey_selector() {
    return this.uses_onboarding_system_sidebar;
  },
  onboarding_system_groups() {
    if (is_applicant_role(this.current_role)) {
      return build_applicant_onboarding_groups(this.onboarding_count_badges, this.onboarding_gate_items);
    }

    const journey_items = this.onboarding_entity_type_items;
    const gate_items = this.onboarding_gate_items;
    const scoped_badges = this.onboarding_count_badges;
    const journey_badges = this.onboarding_journey_count_badges;
    return ONBOARDING_SYSTEM_GROUPS.map(function map_group(group) {
      const badges = get_group_badges(group, scoped_badges, journey_badges);
      return build_onboarding_group(group, journey_items, gate_items, badges);
    });
  },
  onboarding_journey_picker_items() {
    const badges = this.onboarding_journey_count_badges;

    return this.onboarding_entity_type_items.map(function map_journey_item(item) {
      return add_badge_count(item, badges);
    });
  },
  current_onboarding_journey() {
    const selected_journey_id = resolve_selected_journey_id(this.$route);
    const selected_journey_label = resolve_selected_journey_label(this.$route);
    return resolve_current_journey(this.onboarding_journey_picker_items, selected_journey_id, selected_journey_label);
  },
  case_owner_profile() {
    return build_case_owner_profile(this.current_lead_name);
  },
  case_owner_work_items() {
    return CASE_OWNER_WORK_ITEMS;
  },
  case_owner_status_items() {
    return CASE_OWNER_STATUS_ITEMS;
  },
  requestor_work_items() {
    return REQUESTOR_WORK_ITEMS;
  },
  coordinator_work_items() {
    return COORDINATOR_WORK_ITEMS;
  }
};
